import { useState } from 'react';
import api from '@/lib/api';
import { useSede } from '@/lib/sede';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Building2, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const VUOTA = { nome: '', indirizzo: '', citta: '', cap: '', telefono: '', email: '', posti_letto: 0 };

export const SediSection = () => {
  const { sedi, refresh } = useSede();
  const [nuova, setNuova] = useState(VUOTA);
  const [editing, setEditing] = useState(null);
  const [salvataggio, setSalvataggio] = useState(false);

  const crea = async () => {
    if (!nuova.nome.trim()) { toast.error('Inserisci il nome della sede'); return; }
    setSalvataggio(true);
    try {
      await api.post('/sedi', { ...nuova, posti_letto: parseInt(nuova.posti_letto) || 0 });
      toast.success('Sede creata');
      setNuova(VUOTA);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione sede'); }
    setSalvataggio(false);
  };

  const salva = async (sede) => {
    try {
      await api.put(`/sedi/${sede.id}`, {
        nome: sede.nome, indirizzo: sede.indirizzo, citta: sede.citta, cap: sede.cap,
        telefono: sede.telefono, email: sede.email, posti_letto: parseInt(sede.posti_letto) || 0
      });
      toast.success('Sede aggiornata');
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
  };

  const elimina = async (id) => {
    try { await api.delete(`/sedi/${id}`); toast.success('Sede eliminata'); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
  };

  return (
    <Card data-testid="sedi-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Building2 className="w-4 h-4 text-brand" />Sedi della struttura ({sedi.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          {sedi.map(s => (
            <div key={s.id} className="p-3 rounded-lg border border-slate-200" data-testid={`sede-row-${s.id}`}>
              {editing === s.id ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {['nome', 'indirizzo', 'citta', 'cap', 'telefono', 'email', 'posti_letto'].map(campo => (
                    <div key={campo} className="space-y-1">
                      <Label className="text-[10px] capitalize">{campo.replace('_', ' ')}</Label>
                      <Input className="h-8 text-xs" defaultValue={s[campo] || ''}
                        onChange={e => { s[campo] = e.target.value; }} data-testid={`sede-edit-${campo}`} />
                    </div>
                  ))}
                  <div className="flex items-end gap-1">
                    <Button size="sm" className="h-8 bg-brand text-white" onClick={() => salva(s)} data-testid={`sede-save-${s.id}`}>
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(null)}>Annulla</Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{s.nome}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {[s.indirizzo, s.cap, s.citta].filter(Boolean).join(' - ') || 'Indirizzo non indicato'}
                      {s.telefono ? ` · ${s.telefono}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className="bg-brand-soft text-brand-dark text-[10px]">{s.ospiti_presenti || 0} ospiti</Badge>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditing(s.id)} data-testid={`sede-edit-btn-${s.id}`}>Modifica</Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => elimina(s.id)} data-testid={`sede-delete-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Nuova sede</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="space-y-1"><Label className="text-[10px]">Nome *</Label>
              <Input className="h-8 text-xs" value={nuova.nome} onChange={e => setNuova(f => ({ ...f, nome: e.target.value }))} data-testid="nuova-sede-nome" /></div>
            <div className="space-y-1"><Label className="text-[10px]">Indirizzo</Label>
              <Input className="h-8 text-xs" value={nuova.indirizzo} onChange={e => setNuova(f => ({ ...f, indirizzo: e.target.value }))} data-testid="nuova-sede-indirizzo" /></div>
            <div className="space-y-1"><Label className="text-[10px]">Città</Label>
              <Input className="h-8 text-xs" value={nuova.citta} onChange={e => setNuova(f => ({ ...f, citta: e.target.value }))} data-testid="nuova-sede-citta" /></div>
            <div className="space-y-1"><Label className="text-[10px]">Telefono</Label>
              <Input className="h-8 text-xs" value={nuova.telefono} onChange={e => setNuova(f => ({ ...f, telefono: e.target.value }))} /></div>
          </div>
          <Button size="sm" className="mt-3 bg-brand hover:bg-brand-dark text-white" onClick={crea} disabled={salvataggio} data-testid="crea-sede-btn">
            <Plus className="w-4 h-4 mr-1" />Aggiungi sede
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SediSection;
